import { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { useCVData, getMostRecentExperience } from "../../../lib/cv-mock-data";
import {
  useAIGenerateSummary,
  useAIGenerateTitle,
  useAIEnhanceText,
} from "../../../hooks/useOnboardingApi";
import {
  useSparkEnhance,
  SparkEnhanceEntitlementError,
} from "../../../hooks/useSparkEnhance";
import { useToast } from "../../../hooks/useToast";
import {
  WebSaveButton,
  SparkButton,
  SparkPreview,
  type SparkScope,
} from "./web-form-elements";
import { SpinnerGlyph } from "../../common/SpinnerGlyph.web";
import { validateSummary } from "./cv-form-validation";
import { useDirtyGuard } from "../../../hooks/useDirtyGuard";
import { useHydratedValueState } from "./useHydratedFormState";
import { cvChangeNotApplied } from "../../../lib/cv-write-coordinator";

interface EditSummaryFormProps {
  onSave?: () => Promise<void> | void;
  closeOnSave?: boolean;
  ctaLabel?: string;
  /** Hide the Spark "Apply a skill" entry (onboarding uses the style picker). */
  sparkApplySkill?: boolean;
}

// Same AI glyph the native "Suggest with AI" rows use.
const AI_ICON = require("../../../../assets/onboarding/icon-choose-ai.png");
const aiIconSrc =
  typeof AI_ICON === "string"
    ? AI_ICON
    : ((AI_ICON as { uri?: string }).uri ?? (AI_ICON as string));
function isBlank(value: string): boolean {
  return value.trim().length === 0;
}

/** Native-parity "Suggest with AI" / "Re-suggest with AI" trigger (icon + text). */
function SuggestAIRow({
  onClick,
  loading,
  hasValue,
  disabled,
}: {
  onClick: () => void;
  loading: boolean;
  hasValue: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslation("seeker");
  const label = hasValue
    ? t("cv.edit.resuggestAI", { defaultValue: "Re-suggest with AI" })
    : t("cv.edit.suggestAI", { defaultValue: "Suggest with AI" });
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 0",
        background: "none",
        border: 0,
        cursor: disabled || loading ? "default" : "pointer",
        fontFamily: "inherit",
      }}
    >
      {loading ? (
        <SpinnerGlyph color="#5e29ff" size={16} />
      ) : (
        <img
          src={aiIconSrc}
          width={16}
          height={16}
          alt=""
          aria-hidden
          style={{ objectFit: "contain", flexShrink: 0 }}
        />
      )}
      <span
        style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}
      >
        {label}
      </span>
    </button>
  );
}

export function EditSummaryForm({
  onSave,
  closeOnSave = true,
  ctaLabel,
  sparkApplySkill = true,
}: EditSummaryFormProps) {
  const { t } = useTranslation("seeker");
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const generateSummaryMutation = useAIGenerateSummary();
  const generateTitleMutation = useAIGenerateTitle();
  const enhanceTextMutation = useAIEnhanceText();
  const spark = useSparkEnhance();

  const [headline, setHeadline] = useHydratedValueState(
    profile.headline,
    isBlank,
  );
  const [summary, setSummary] = useHydratedValueState(profile.summary, isBlank);
  const [pendingSummary, setPendingSummary] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState<string | null>(null);

  const errors = useMemo(
    () => validateSummary({ headline, summary }, t),
    [headline, summary, t],
  );
  const hasErrors = Boolean(errors.headline || errors.summary);

  // Build full CV context for the dependency chain
  const buildCVContext = () => ({
    name: profile.name,
    title: headline || profile.headline,
    experience: (profile.experience || []).map((e: any) => ({
      title: e.role || e.title,
      company: e.company,
      startDate: e.startDate,
      endDate: e.endDate,
      current: e.current,
      employmentType: e.employmentType,
      location: e.location,
      locationType: e.locationType,
      description: e.overview || e.description || "",
      achievements: e.bullets || e.achievements || [],
    })),
    education: (profile.education || []).map((e: any) => ({
      school: e.school,
      degree: e.degree,
      fieldOfStudy: e.fieldOfStudy || e.field,
    })),
    certifications: (profile.certifications || []).map((c: any) => ({
      name: c.name,
      issuer: c.issuer,
    })),
    skills:
      profile.skillGroups?.flatMap((g: any) => g.items || g.skills || []) || [],
    careerHighlights: (profile.highlights || []).map((h: any) => ({
      title: h.title,
      description: h.desc || h.description || "",
    })),
  });

  const handleSave = async () => {
    if (isSaving || hasErrors || aiLoading !== null) return;
    const effectiveSummary = pendingSummary ?? summary;
    if (pendingSummary) setPendingSummary(null);
    updateProfile({ headline, summary: effectiveSummary });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        toast.success(t("cv.edit.saved"));
        if (closeOnSave && router.canGoBack()) router.back();
      } catch (error) {
        // B02: a CV conflict the user answered is not a failure. "Discard"
        // keeps the version saved elsewhere and closes past the unsaved-
        // changes guard; "keep editing" keeps this draft open.
        const outcome = cvChangeNotApplied(error);
        if (outcome) {
          if (outcome === "discarded" && closeOnSave) leave();
          return false;
        }
        toast.error(t("cv.edit.saveFailed"));
      } finally {
        setIsSaving(false);
      }
    } else {
      router.back();
    }
  };

  const isDirty =
    headline !== profile.headline ||
    summary !== profile.summary ||
    pendingSummary !== null;
  const { leave } = useDirtyGuard(isDirty, handleSave);

  const handleAIHeadline = async () => {
    setAiLoading("headline");
    try {
      const ctx = buildCVContext();
      const recent = getMostRecentExperience(ctx.experience);
      const result = await generateTitleMutation.mutateAsync({
        ...ctx,
        latestJobTitle: recent?.title,
        topSkills: ctx.skills.slice(0, 5),
        summary: summary || undefined,
      });
      if (result.titleOptions?.length) setHeadline(result.titleOptions[0]);
      else
        toast.error(
          "Generation failed",
          "AI returned an empty result. Please try again.",
        );
    } catch (err) {
      toast.error(
        "Generation failed",
        err instanceof Error ? err.message : "Could not generate title",
      );
    } finally {
      setAiLoading(null);
    }
  };

  const handleAISummary = async (scope: SparkScope = "section") => {
    setAiLoading("summary");
    try {
      const ctx = buildCVContext();
      if (summary) {
        // Improve existing summary: gated by the Spark tier limit.
        await spark.ensureAllowed();
        try {
          const result = await enhanceTextMutation.mutateAsync({
            text: summary,
            field: "summary",
            scope,
            context: { title: ctx.title, skills: ctx.skills.slice(0, 10) },
          });
          if (result.enhanced) setPendingSummary(result.enhanced);
          else
            toast.error(
              "Generation failed",
              "AI returned an empty result. Please try again.",
            );
        } finally {
          await spark.reconcile();
        }
      } else {
        // Empty field → nothing to preserve, so apply the generated summary
        // directly instead of prompting to keep/replace.
        const result = await generateSummaryMutation.mutateAsync(ctx);
        if (result.summary) setSummary(result.summary);
        else
          toast.error(
            "Generation failed",
            "AI returned an empty result. Please try again.",
          );
      }
    } catch (err) {
      // At the limit the upgrade modal already opened: stop silently.
      if (err instanceof SparkEnhanceEntitlementError) return;
      toast.error(
        "Generation failed",
        err instanceof Error ? err.message : "Could not generate summary",
      );
    } finally {
      setAiLoading(null);
    }
  };

  const fieldLabelStyle = {
    fontSize: 12,
    letterSpacing: "-0.12px",
    color: "var(--text-tertiary)",
  } as const;
  const fieldInputStyle = {
    fontSize: 16,
    border: "none",
    background: "transparent",
    outline: "none",
    fontFamily: "inherit",
    color: "var(--text-primary)",
    padding: 0,
    width: "100%",
    boxSizing: "border-box",
  } as const;
  const cardStyle = {
    border: "1px solid var(--input-border)",
    borderRadius: 16,
    paddingBottom: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
  } as const;
  const errorStyle = {
    fontSize: 12,
    color: "#d93025",
    alignSelf: "flex-start",
    paddingInline: 8,
  } as const;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginTop: 12,
      }}
    >
      {/* Professional title */}
      <div style={cardStyle}>
        <div
          style={{
            width: "100%",
            minHeight: 60,
            borderRadius: 16,
            paddingInline: 16,
            paddingBlock: 8,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
            background: "rgba(0,0,0,0.04)",
            boxSizing: "border-box",
          }}
        >
          <label style={fieldLabelStyle}>
            {t("cv.edit.professionalTitle", {
              defaultValue: "Professional title",
            })}
          </label>
          <input
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder={t("cv.edit.headline", {
              defaultValue: "example: HR & Technology Expert",
            })}
            style={fieldInputStyle}
          />
        </div>
        {errors.headline ? (
          <span style={errorStyle}>{errors.headline}</span>
        ) : null}
        <SuggestAIRow
          onClick={handleAIHeadline}
          loading={aiLoading === "headline"}
          hasValue={!!headline}
          disabled={aiLoading !== null}
        />
      </div>

      {/* Summary */}
      <div style={cardStyle}>
        <div
          style={{
            width: "100%",
            minHeight: 188,
            borderRadius: 16,
            paddingInline: 16,
            paddingBlock: 12,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            gap: 2,
            background: "rgba(0,0,0,0.04)",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <label style={fieldLabelStyle}>
              {t("cv.edit.summaryLabel", { defaultValue: "Summary" })}
            </label>
            <span style={fieldLabelStyle}>{summary.length} chars</span>
          </div>
          <textarea
            value={summary}
            onChange={(e) => {
              setSummary(e.target.value);
              if (pendingSummary) setPendingSummary(null);
            }}
            placeholder={t("cv.edit.summaryText", {
              defaultValue: "Tip: write your professional summary",
            })}
            dir="auto"
            style={{
              ...fieldInputStyle,
              flex: 1,
              minHeight: 150,
              marginTop: 6,
              resize: "none",
            }}
          />
        </div>
        {errors.summary ? (
          <span style={errorStyle}>{errors.summary}</span>
        ) : null}
        {pendingSummary ? (
          <div style={{ width: "100%", paddingInline: 0 }}>
            <SparkPreview
              text={pendingSummary}
              onAccept={() => {
                setSummary(pendingSummary);
                setPendingSummary(null);
              }}
              onReject={() => setPendingSummary(null)}
            />
          </div>
        ) : null}
        {summary ? (
          <SparkButton
            onEnhance={handleAISummary}
            loading={aiLoading === "summary"}
            disabled={aiLoading !== null}
            showApplySkill={sparkApplySkill}
          />
        ) : (
          <SuggestAIRow
            onClick={() => handleAISummary("section")}
            loading={aiLoading === "summary"}
            hasValue={!!summary}
            disabled={aiLoading !== null}
          />
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <WebSaveButton
          label={ctaLabel ?? t("cv.edit.save")}
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || hasErrors || aiLoading !== null}
        />
      </div>
    </div>
  );
}
